import type { FunnelPageItem, FunnelRoutingEvent } from '../types';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import { getLinearNextPage } from './funnelRouting';

/**
 * Pages reachable from step 1 by walking both routing branches and the
 * linear fallback. Anything not in this set is an orphan.
 */
export function getReachablePageIds(pages: FunnelPageItem[]): Set<number> {
    const reachable = new Set<number>();
    const savedPages = pages.filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID);
    if (savedPages.length === 0) return reachable;

    const entry = [...savedPages].sort((a, b) => a.order - b.order)[0];
    if (!entry) return reachable;

    const queue: number[] = [entry.page_id];
    while (queue.length) {
        const id = queue.shift()!;
        if (reachable.has(id)) continue;
        reachable.add(id);
        const page = savedPages.find(p => p.page_id === id);
        if (!page) continue;

        const nextIds: number[] = [];
        (['on_purchase', 'on_skip'] as FunnelRoutingEvent[]).forEach(event => {
            const target = page.routing?.[event];
            if (target && savedPages.some(p => p.page_id === target.page_id)) {
                nextIds.push(target.page_id);
            }
        });
        const linearNext = getLinearNextPage(savedPages, page.order);
        if (linearNext) nextIds.push(linearNext.page_id);

        nextIds.forEach(nid => {
            if (!reachable.has(nid)) queue.push(nid);
        });
    }
    return reachable;
}

/** DFS cycle detection over explicit routing edges (ignores linear fallback). */
export function findRoutingCycles(pages: FunnelPageItem[]): number[][] {
    const byId = new Map(pages.map(p => [p.page_id, p]));
    const cycles: number[][] = [];

    const WHITE = 0,
        GRAY = 1,
        BLACK = 2;
    const color = new Map<number, number>();
    pages.forEach(p => color.set(p.page_id, WHITE));

    const stack: number[] = [];

    function dfs(id: number) {
        color.set(id, GRAY);
        stack.push(id);
        const page = byId.get(id);
        const neighbors: number[] = [];
        if (page?.routing) {
            (['on_purchase', 'on_skip'] as FunnelRoutingEvent[]).forEach(event => {
                const target = page.routing?.[event];
                if (target && byId.has(target.page_id)) neighbors.push(target.page_id);
            });
        }
        for (const n of neighbors) {
            const c = color.get(n);
            if (c === GRAY) {
                const start = stack.indexOf(n);
                cycles.push(stack.slice(start));
            } else if (c === WHITE) {
                dfs(n);
            }
        }
        color.set(id, BLACK);
        stack.pop();
    }

    pages.forEach(p => {
        if (color.get(p.page_id) === WHITE) dfs(p.page_id);
    });
    return cycles;
}
